<template>
	<div>
		<div class="main text-left">
			<div v-if="!isEditMode">
				<div class="font-bold mb-4">{{ $t('classify_vehicle_by_assessor_input') }}</div>
				<div class="mb-4">{{ isAppContainer ? $t('scan_or_upload_vehicle_registration') : $t('upload_vehicle_registration') }}</div>
				<div class="mb-4">
					<div class="flex justify-center" v-if="vehicle.registrationImage.base64Content">
						<BaseImage :src="vehicle.registrationImage.base64Content" :alt="$t('vehicle_registration')" class="mb-4 w-full" />
					</div>
					<div class="flex flex-grow justify-center">
						<BaseCamera
							orientation="landscape"
							@snapshot="vehicle.registrationImage.base64Content = $event"
							:label="$t('add_vehicle_registration')"
							:take-image-label="$t('activate_camera_for_registration_image')"
							:remove-upload-button="isAppContainer"
							:take-image="true"
							:allow-upload-in-app-container="false"
							:enable-preview="false"
							accept="image/*, application/pdf"
						/>
					</div>
				</div>
				<div class="mb-4">
					{{ $t('check_fields_input') }}
				</div>
			</div>
			<div>
				<div class="w-full">
					<div class="w-full flex justify-center">
						<LicensePlateInput ref="LicensePlateInput" v-model="vehicle.licensePlate" show-only="regular" />
					</div>
				</div>
				<div class="md:flex md:space-x-4">
					<div class="md:w-1/2">
						<AutocompleteInput
							ref="fahrzeughersteller"
							v-model="vehicle.manufacturer"
							:required="true"
							:label="$t('manufacturer_sample')"
							mask="AXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
							@input="updateManufacturer"
							@selectValue="selectManufacturer"
							:options="manufacturerList"
							@blur="manufacturerList = null"
						/>
					</div>
					<div class="md:w-1/2">
						<AutocompleteInput
							ref="BaseInput.value.model"
							v-model="vehicle.model"
							:required="true"
							:label="$t('model_sample')"
							mask="BXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
							@input="updateModel"
							@click="updateModel('')"
							@selectValue="selectModel"
							:options="modelList"
							@blur="modelList = null"
						/>
					</div>
				</div>
				<div class="xl:flex md:space-x-4" v-if="getAuthenticatedUserGroupLevel === '1'">
					<div class="w-1/2">
						<BaseSelect :label="$t('class')" :options="rentalCarClasses" v-model="selectedRentalCarClass" :required="true" ref="baseSelectCarClass" />
					</div>
					<div class="w-1/2">
						<BaseInput
							ref="baseInputVehicleIdentificationNumber"
							:label="$t('vin')"
							:placeholder="$t('vin')"
							class="w-full pr-0"
							type="text"
							validation="[^IOQ]{17}"
							mask="FFFFFFFFFFFFFFFFF"
							maxlength="17"
							:error-message="$t('enter_valid_vin')"
							v-model="vehicle.vehicleIdentificationNumber"
							:required="false"
						/>
					</div>
				</div>
				<div class="xl:flex" v-else>
					<BaseInput
						ref="baseInputvehicleIdentificationNumber2"
						type="hidden"
						validation="[^IOQ]{17}"
						mask="BBBBBBBBBBBBBBBBB"
						maxlength="17"
						v-model="vehicle.vehicleIdentificationNumber"
						:required="false"
					/>
				</div>
			</div>
			<Footer class="justify-between" :is-back-button="true" @backButtonClick="$router.push({ name: 'rentalcars' })" v-if="showFooter">
				<template #continueButton>
					<SaveButton style="min-width: fit-content" @click="save" class="w-16 btn-blue">
						{{ isEditMode ? $t('save_rental_car_classification') : $t('apply_vehicle_classification') }}
					</SaveButton>
				</template>
			</Footer>
		</div>
		<div v-if="isEditMode && vehicle.registrationImage && vehicle.registrationImage.base64Content" class="flex justify-center mb-8">
			<img :src="`${vehicle.registrationImage.base64Content}`" :alt="$t('vehicle_registration')" :title="$t('vehicle_registration')" />
		</div>
	</div>
</template>

<script>
import BaseInput from '@/components/forms/inputs/BaseInput';
import { VehicleApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import LicensePlateInput from '@/components/forms/inputs/LicensePlateInput';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import { getOptionFromOptionsArray } from '@/plugins/Utils';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import Validate from '@/mixins/Validate';
import Footer from '@/components/damagecases/Footer';
import SaveButton from '@/components/forms/buttons/SaveButton';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import BaseImage from '@/components/forms/BaseImage.vue';
import BaseCamera from '@/components/forms/inputs/BaseCamera.vue';
import VehicleRepository from '@/api/repositories/VehicleRepository';
import AutocompleteInput from '@/components/forms/inputs/AutocompleteInput.vue';

export default {
	name: 'RentalCarForm',
	components: { AutocompleteInput, BaseCamera, BaseImage, SaveButton, BaseSelect, LicensePlateInput, BaseInput, Footer },
	mixins: [LoadingSpinner, Validate, AppContainerMixin],
	data() {
		return {
			isUploading: false,
			vehicle: {
				registrationImage: {
					base64Content: null,
				},
				licensePlate: null,
				manufacturer: null,
				model: null,
				vehicleIdentificationNumber: null,
				rentalCarClass: null,
			},
			updateModelTimeoutId: null,
			updateManufacturerTimeoutId: null,
			selectedManufacturer: null,
			selectedModel: null,
			manufacturerList: [],
			modelList: [],
			selectedRentalCarClass: null,
			index: null,
			rentalCarClasses: [
				{ display: '1', value: 1 },
				{ display: '2', value: 2 },
				{ display: '3', value: 3 },
				{ display: '4', value: 4 },
				{ display: '5', value: 5 },
				{ display: '6', value: 6 },
				{ display: '7', value: 7 },
				{ display: '8', value: 8 },
				{ display: '9', value: 9 },
				{ display: '10', value: 10 },
			],
		};
	},
	props: {
		showFooter: { default: true, required: false },
	},
	mounted() {
		if (this.showFooter) {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
		}

		if (this.isEditMode) {
			this.loadVehicleData();
		}
	},
	computed: {
		isSaveAllowed() {
			return (
				this.checkInput(this.vehicle.licensePlate) &&
				this.checkInput(this.vehicle.manufacturer) &&
				this.checkInput(this.vehicle.model) &&
				this.checkInput(this.vehicle.registrationImage.base64Content)
			);
		},
		isEditMode() {
			return this.checkEditMode();
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
	},
	methods: {
		checkEditMode() {
			return !!(this.$route.params.vehicleId && this.$store.getters.getAuthenticatedUserGroupLevel === '1');
		},
		loadVehicleData: function() {
			VehicleApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.rentalcarsIdGet(this.$route.params.vehicleId)
				.then(result => {
					this.vehicle = result.data;
					this.selectedRentalCarClass = getOptionFromOptionsArray(this.rentalCarClasses, this.vehicle.rentalCarClass);
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), 'Der Mietwagen konnte nicht geladen werden', 'error');
				});
		},
		checkInput: function(input) {
			return !(input === null || input === '');
		},
		updateManufacturer(value) {
			if (value === this.selectedManufacturer) return;
			this.selectedManufacturer = null;
			clearTimeout(this.updateManufacturerTimeoutId);
			this.updateManufacturerTimeoutId = setTimeout(() => {
				new VehicleRepository().getManufacturerNames(value).then(response => {
					this.manufacturerList = response.data?.map(manufacturer => manufacturer.name);
				});
			}, 300);
		},
		updateModel(value) {
			this.modelList = null;
			if (value === this.selectedModel) return;
			clearTimeout(this.updateModelTimeoutId);
			this.updateModelTimeoutId = setTimeout(() => {
				new VehicleRepository().getModelNames(value, this.vehicle.manufacturer).then(response => {
					this.modelList = response.data?.map(model => model.name);
				});
			}, 300);
		},
		selectManufacturer(manufacturer) {
			this.selectedManufacturer = manufacturer;
			this.vehicle.manufacturer = manufacturer;
			clearTimeout(this.updateManufacturerTimeoutId);
		},
		selectModel(model) {
			this.selectedModel = model;
			this.vehicle.model = model;
			clearTimeout(this.updateModelTimeoutId);
		},
		save: function() {
			if (this.isEditMode) {
				return this.updateVehicle();
			}
			if (this.validate()) {
				this.setLoadingStatus(true);
				if (this.selectedRentalCarClass !== null) {
					this.vehicle.rentalCarClass = this.selectedRentalCarClass?.value;
				}
				if (!this.vehicle.registrationImage.base64Content) {
					this.$swal(this.$t('general.error').toString(), this.$t('vehicle_registration_missing').toString(), 'error');
					this.setLoadingStatus(false);
				} else {
					VehicleApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.rentalcarsPost({ ...this.vehicle })
						.then(result => {
							this.$store.commit('setConfirmLeaving', false);
							this.setLoadingStatus(false);
							this.$swal(this.$t('general.success').toString(), this.$t('applied_vehicle_classification').toString(), 'success').then(_ => {
								this.$router.go(-1);
							});
						})
						.catch(error => {
							this.setLoadingStatus(false);
							this.$swal(this.$t('general.error').toString(), this.$t('apply_vehicle_error').toString(), 'error');
						});
				}
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		updateVehicle: function() {
			if (this.validate()) {
				this.setLoadingStatus(true);
				if (this.selectedRentalCarClass !== null) {
					this.vehicle.rentalCarClass = this.selectedRentalCarClass?.value;
				}
				VehicleApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.rentalcarsIdPut(this.$route.params.vehicleId, this.vehicle)
					.then(result => {
						this.$store.commit('setConfirmLeaving', false);
						this.setLoadingStatus(false);
						this.$swal(this.$t('general.success').toString(), this.$t('values_saved').toString(), 'success').then(
							this.$router.push({ name: 'rentalcars' }),
						);
					})
					.catch(error => {
						this.setLoadingStatus(false);
						this.$swal(this.$t('general.error').toString(), this.$t('values_save_error').toString(), 'error');
					});
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		setVehicleRegistrationImageContent: function($event) {
			if ($event.manufacturer) {
				this.vehicle.manufacturer = $event.manufacturer;
			}
			if ($event.VIN) {
				this.vehicle.vehicleIdentificationNumber = $event.VIN;
			}
		},
	},
};
</script>

<style scoped></style>
